import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth'

export function useBillingData() {
  const authStore = useAuthStore()
  const { user } = storeToRefs(authStore)

  const billingData = ref({
    firstName: '',
    lastName: '',
    company: '',
    street: '',
    number: '',
    zip: '',
    city: '',
    country: '',
    email: '',
    vatId: '',
    taxId: '',
    saveForFuture: true
  })

  const hasFilledBillingData = computed(() => {
    const data = billingData.value
    return !!(
      data.firstName &&
      data.lastName &&
      data.street &&
      data.number &&
      data.zip &&
      data.city &&
      data.country &&
      data.email
    )
  })

  const validateBillingData = () => {
    const data = billingData.value
    return !!(
      data.firstName &&
      data.lastName &&
      data.street &&
      data.number &&
      data.zip &&
      data.city &&
      data.country &&
      data.email &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)
    )
  }

  const applyUserDefaults = () => {
    if (!user.value) return

    const updatedData = { ...billingData.value }
    let changed = false

    if (!updatedData.firstName && user.value.firstName) {
      updatedData.firstName = user.value.firstName
      changed = true
    }

    if (!updatedData.lastName && user.value.lastName) {
      updatedData.lastName = user.value.lastName
      changed = true
    }

    if (!updatedData.email && user.value.email) {
      updatedData.email = user.value.email
      changed = true
    }

    if (!updatedData.country) {
      updatedData.country = 'Deutschland'
      changed = true
    }

    if (changed) {
      billingData.value = updatedData
    }
  }

  const loadLastBillingData = async () => {
    try {
      const { getInvoiceData } = await import('@/api/invoiceData')
      const response = await getInvoiceData()
      if (response.success && response.data.invoiceData) {
        const data = response.data.invoiceData
        billingData.value = {
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          company: data.company || data.companyName || '',
          street: data.street || '',
          number: data.number || data.houseNumber || '',
          zip: data.zip || data.zipCode || data.postalCode || '',
          city: data.city || '',
          country: data.country || 'Deutschland',
          email: data.email || '',
          vatId: data.vatId || '',
          taxId: data.taxId || '',
          saveForFuture: true
        }
      } else {
        applyUserDefaults()
      }
    } catch (error) {
      console.warn('Konnte Rechnungsdaten nicht laden:', error)
      applyUserDefaults()
    }
  }

  watch(user, applyUserDefaults, { immediate: true })

  const getBillingDataForApi = () => ({
    firstName: billingData.value.firstName,
    lastName: billingData.value.lastName,
    company: billingData.value.company,
    companyName: billingData.value.company,
    street: billingData.value.street,
    number: billingData.value.number,
    houseNumber: billingData.value.number,
    zip: billingData.value.zip,
    postalCode: billingData.value.zip,
    city: billingData.value.city,
    country: billingData.value.country,
    email: billingData.value.email,
    vatId: billingData.value.vatId,
    taxId: billingData.value.taxId
  })

  return {
    billingData,
    hasFilledBillingData,
    validateBillingData,
    loadLastBillingData,
    getBillingDataForApi
  }
}
